import { PageContainer } from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { CompetencyMatrix } from './components/CompetencyMatrix';
import { LevelLegend } from './components/LevelLegend';
import { MatrixHighlights } from './components/MatrixHighlights';
import { ReportToolbar } from './components/ReportToolbar';
import { buildReportFilterParams } from './utils/reportFilterParams';

import type { CompetencyMatrixReportParams } from '@/types/reports';

import { useGetCompetencyProfiles } from '@/api/competency-services/competency-profile';
import { useGetCompanyInformation } from '@/api/directory';
import { useGetCompetencyMatrixReport } from '@/api/reports';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import {
    defaultFiltersState,
    type SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';

/**
 * "Rapport des compétences" container: owns the filter/profile selection, the report query built
 * from them, and renders the toolbar, the three highlight cards, the matrix and the legend — or a
 * loading, failure or empty state while there is nothing to show. A failed request never reads as
 * "no employees", which would state a fact the page does not know; a failed refresh keeps the
 * report already on screen.
 */
export const CompetencyMatrixReportPage = () => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const { data: company } = useGetCompanyInformation();
    const { data: profilesData } = useGetCompetencyProfiles();

    const [filters, setFilters] = useState<SelectedFilters>(defaultFiltersState);
    const [competencyProfileId, setCompetencyProfileId] = useState<string | undefined>(undefined);

    const activeProfiles = useMemo(
        () => (profilesData ?? []).filter((profile) => profile.isActive),
        [profilesData],
    );

    const params = useMemo<CompetencyMatrixReportParams>(
        () => buildReportFilterParams({ filters, company, competencyProfileId }),
        [filters, company, competencyProfileId],
    );

    const { data: report, isLoading, isError } = useGetCompetencyMatrixReport(params);

    useFetchErrorsHandler({ errors: [isError] });

    return (
        <PageContainer>
            <div className={'flex flex-col gap-6'}>
                <ReportToolbar
                    company={company}
                    onFiltersChange={setFilters}
                    profiles={activeProfiles}
                    selectedProfileId={competencyProfileId}
                    onProfileChange={setCompetencyProfileId}
                />

                {isLoading ? (
                    <Skeleton className={'flex flex-col gap-4'}>
                        <div className={'grid grid-cols-1 gap-4 sm:grid-cols-3'}>
                            <Bone className={'h-24 w-full'} />
                            <Bone className={'h-24 w-full'} />
                            <Bone className={'h-24 w-full'} />
                        </div>

                        <Bone className={'h-80 w-full'} />
                    </Skeleton>
                ) : !report ? (
                    <EmptyState
                        title={t('reports.competencyMatrix.loadError.title')}
                        message={t('reports.competencyMatrix.loadError.message')}
                    />
                ) : report.employees.length === 0 ? (
                    <EmptyState
                        title={t('reports.competencyMatrix.empty.title')}
                        message={t('reports.competencyMatrix.empty.message')}
                    />
                ) : (
                    <>
                        <MatrixHighlights
                            report={report}
                            language={language}
                        />

                        <CompetencyMatrix
                            report={report}
                            language={language}
                        />

                        <LevelLegend levels={report.levels} />
                    </>
                )}
            </div>
        </PageContainer>
    );
};
